import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Wallet, ArrowRight, ShieldCheck, TrendingUp } from 'lucide-react';
import { AUTH } from '@/constants/testIds';
import { formatApiErrorDetail } from '@/lib/api';

export default function LoginPage() {
  const nav = useNavigate();
  const { login } = useAuth();
  const [email, setEmail] = useState('admin@demo.com');
  const [password, setPassword] = useState('admin123');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(email, password);
      nav('/dashboard', { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const onGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const onSubmitDemo = async () => {
    setEmail('admin@demo.com');
    setPassword('admin123');
    setError('');
    setLoading(true);
    try {
      await login('admin@demo.com', 'admin123');
      nav('/dashboard', { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen w-full grid lg:grid-cols-2 bg-background">
      {/* LEFT — brand panel */}
      <div className="hidden lg:flex relative overflow-hidden bg-[#090D16] text-white">
        <div className="absolute inset-0 grid-bg opacity-40" />
        <div className="absolute -top-20 -left-20 h-96 w-96 rounded-full bg-emerald-500/15 blur-3xl" />
        <div className="absolute bottom-0 right-0 h-96 w-96 rounded-full bg-blue-500/15 blur-3xl" />
        <div className="relative z-10 flex flex-col justify-between p-12 w-full">
          <div className="flex items-center gap-3">
            <div className="h-11 w-11 rounded-xl bg-gradient-to-br from-emerald-400 to-emerald-600 grid place-items-center shadow-lg shadow-emerald-500/30">
              <Wallet className="h-6 w-6 text-white" />
            </div>
            <div>
              <div className="font-heading text-lg font-semibold tracking-tight">Ledgerly</div>
              <div className="text-xs text-white/50 font-mono">Gestão Financeira</div>
            </div>
          </div>
          <div className="space-y-6 max-w-md">
            <h1 className="font-heading text-4xl xl:text-5xl font-bold leading-tight">
              O controle financeiro que sua empresa merece.
            </h1>
            <p className="text-white/60 text-base leading-relaxed">
              Organize lançamentos, acompanhe o fluxo de caixa em tempo real, guarde comprovantes e tenha
              relatórios e histórico completos — tudo em um só lugar.
            </p>
            <div className="grid gap-3 pt-4">
              <div className="flex items-center gap-3 text-sm text-white/70">
                <div className="h-8 w-8 rounded-lg bg-emerald-500/10 border border-emerald-500/20 grid place-items-center"><ShieldCheck className="h-4 w-4 text-emerald-400" /></div>
                Seus dados protegidos e organizados para sua equipe
              </div>
              <div className="flex items-center gap-3 text-sm text-white/70">
                <div className="h-8 w-8 rounded-lg bg-blue-500/10 border border-blue-500/20 grid place-items-center"><TrendingUp className="h-4 w-4 text-blue-400" /></div>
                Fluxo de caixa, DRE e relatórios instantâneos
              </div>
            </div>
          </div>
          <div className="text-xs text-white/40 font-mono">© 2026 Ledgerly · pt-BR</div>
        </div>
      </div>

      {/* RIGHT — login */}
      <div className="flex items-center justify-center px-6 py-12 sm:px-12">
        <div className="w-full max-w-md space-y-8">
          <div>
            <div className="lg:hidden flex items-center gap-2 mb-6">
              <div className="h-9 w-9 rounded-lg bg-emerald-500 grid place-items-center"><Wallet className="h-5 w-5 text-white" /></div>
              <span className="font-heading font-semibold text-lg">Ledgerly</span>
            </div>
            <h2 className="font-heading text-3xl font-bold tracking-tight">Bem-vindo de volta</h2>
            <p className="text-muted-foreground text-sm mt-2">Entre com seu email corporativo para acessar sua empresa.</p>
          </div>

          <form onSubmit={onSubmit} data-testid={AUTH.loginForm} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input id="email" data-testid={AUTH.loginEmail} type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="voce@empresa.com" autoComplete="email" required />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Senha</Label>
              <Input id="password" data-testid={AUTH.loginPassword} type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" autoComplete="current-password" required />
            </div>
            {error && <div data-testid={AUTH.error} className="text-sm text-rose-500 bg-rose-500/10 border border-rose-500/20 rounded-md px-3 py-2">{error}</div>}
            <Button type="submit" data-testid={AUTH.loginSubmit} disabled={loading} className="w-full h-11 bg-emerald-600 hover:bg-emerald-700 text-white font-medium">
              {loading ? 'Entrando…' : (<>Entrar <ArrowRight className="ml-2 h-4 w-4" /></>)}
            </Button>
          </form>

          <div className="relative">
            <div className="absolute inset-0 flex items-center"><span className="w-full border-t" /></div>
            <div className="relative flex justify-center text-xs uppercase"><span className="bg-background px-3 text-muted-foreground tracking-wider">ou</span></div>
          </div>

          <Button type="button" data-testid={AUTH.googleLogin} onClick={onGoogle} variant="outline" className="w-full h-11 gap-3 font-medium">
            <svg width="18" height="18" viewBox="0 0 48 48"><path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/><path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/><path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/><path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/></svg>
            Continuar com Google
          </Button>

          <div className="text-sm text-center text-muted-foreground">
            Ainda não tem empresa cadastrada?{' '}
            <Link to="/register" data-testid={AUTH.goRegister} className="text-emerald-600 dark:text-emerald-400 hover:underline font-medium">Criar minha empresa</Link>
          </div>

          <div className="mt-6 rounded-xl border border-dashed border-emerald-500/40 bg-emerald-500/5 p-4">
            <div className="flex items-center justify-between gap-3">
              <div className="space-y-1">
                <div className="font-semibold text-sm text-emerald-700 dark:text-emerald-400">Demo:</div>
                <div className="font-mono text-xs text-muted-foreground">admin@demo.com / admin123</div>
              </div>
              <Button type="button" data-testid="demoLogin" onClick={onSubmitDemo} disabled={loading} variant="outline" size="sm" className="shrink-0 font-medium text-emerald-700 dark:text-emerald-400 border-emerald-500/40 hover:bg-emerald-500/10">
                {loading ? 'Entrando…' : 'Entrar na demo'}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
